import {Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, signal} from '@angular/core';

const MAX_COMMENT_LENGTH = 2000;

/**
 * Optional note attached to a rating: a textarea with a live `n / 2000`
 * counter. Submit is disabled past the limit (mirrors the server-side 400) and
 * when nothing changed.
 *
 * Stateless w.r.t. persistence — it emits `save`; the parent fires the PUT
 * (a comment is always sent together with the current score).
 */
@Component({
  selector: 'app-rating-comment-box',
  standalone: true,
  imports: [],
  template: `
    <div class="mt-3">
      <textarea
        class="textarea textarea-bordered w-full text-base"
        rows="3"
        [placeholder]="placeholder"
        [value]="draft()"
        [disabled]="disabled"
        [attr.maxlength]="max"
        (input)="onInput($event)"></textarea>

      <div class="flex items-center justify-between mt-1">
        <span class="text-xs"
              [class.text-error]="draft().length > max"
              [class.text-base-content]="draft().length <= max"
              [class.opacity-50]="draft().length <= max">
          {{ draft().length }} / {{ max }}
        </span>

        <div class="flex items-center gap-2">
          @if (dirty()) {
            <button
              type="button"
              class="btn btn-ghost btn-sm"
              [disabled]="disabled"
              (click)="reset()">
              Cancel
            </button>
          }
          <button
            type="button"
            class="btn btn-primary btn-sm"
            [disabled]="disabled || !dirty() || draft().length > max"
            (click)="submit()">
            @if (disabled) {
              <span class="loading loading-spinner loading-xs"></span>
            }
            {{ initial ? 'Update note' : 'Add note' }}
          </button>
        </div>
      </div>
    </div>
  `,
})
export class RatingCommentBoxComponent implements OnChanges {
  readonly max = MAX_COMMENT_LENGTH;

  /** The persisted comment, if any. Resets the draft when it changes. */
  @Input() initial = '';

  /** Disable while a request is in flight. */
  @Input() disabled = false;

  @Input() placeholder = 'Add a note (optional) — e.g. "too salty, use less next time."';

  /** Emits the trimmed comment to persist. */
  @Output() save = new EventEmitter<string>();

  readonly draft = signal('');

  ngOnChanges(changes: SimpleChanges): void {
    // Only re-seed the draft when the persisted comment itself changes, so a
    // `disabled` toggle mid-save doesn't wipe what the user is typing.
    if (changes['initial']) {
      this.draft.set(this.initial ?? '');
    }
  }

  dirty(): boolean {
    return this.draft() !== (this.initial ?? '');
  }

  onInput(event: Event): void {
    this.draft.set((event.target as HTMLTextAreaElement).value);
  }

  reset(): void {
    this.draft.set(this.initial ?? '');
  }

  submit(): void {
    if (this.disabled || this.draft().length > this.max || !this.dirty()) return;
    this.save.emit(this.draft().trim());
  }
}
